import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { shopService } from '../../services/index.js';
import { LoadingSpinner } from '../../components/index.js';
import './Shops.css';

/**
 * "Postcards Left" cell — the shop's remaining postcard balance from Service
 * Intel (NcaPostcardsPurchasedQty, decremented as batches are created).
 *
 * A dash is shown when the number is genuinely UNKNOWN (shop isn't a Service
 * Intel location, or SI couldn't be reached) — never a "0", which would read as
 * "this client is out of postcards" and is a very different statement.
 */
const renderPostcardsLeft = (quota) => {
  if (!quota?.applicable || !quota?.enforced || quota?.remaining == null) {
    return <span className="postcards-cell-shops unknown-shops">—</span>;
  }
  const left = Number(quota.remaining);
  return (
    <span className={`postcards-cell-shops ${left === 0 ? 'empty-shops' : ''}`}>
      {left.toLocaleString()}
    </span>
  );
};

export const Shops = () => {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [uniqueStates, setUniqueStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  // Default to shops that already have lat/lng (already geocoded).
  // 'geocoded' | 'not-geocoded' | 'all'
  const [geocodeFilter, setGeocodeFilter] = useState('geocoded');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const fetchShops = async (page = 1, overrides = {}) => {
    try {
      setLoading(true);
      const effSearch = overrides.search ?? search;
      const effState = overrides.stateFilter ?? stateFilter;
      const effGeocode = overrides.geocodeFilter ?? geocodeFilter;

      // withQuota → the backend attaches each shop's remaining postcard balance
      // (the "Postcards Left" column). Opt-in, since it may refresh stale
      // balances from Service Intel.
      const params = { page, limit: 20, withQuota: 1 };
      if (effSearch) params.search = effSearch;
      if (effState) params.state = effState;
      if (effGeocode === 'geocoded') params.isGeocoded = true;
      else if (effGeocode === 'not-geocoded') params.isGeocoded = false;
      // 'all' → no isGeocoded param → backend returns every shop

      const shopsRes = await shopService.getShops(params);

      setShops(shopsRes.data.shops);
      setPagination(shopsRes.data.pagination);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load shops');
    } finally {
      setLoading(false);
    }
  };

  // Fetch states once on mount for the filter dropdown
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const statsRes = await shopService.getShopStats();
        const states = statsRes.data.stats?.byState?.map((s) => s._id).sort() || [];
        setUniqueStates(states);
      } catch {
        // Non-critical — filter dropdown just won't have options
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    fetchShops();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchShops(1);
  };

  const handlePageChange = (newPage) => {
    fetchShops(newPage);
  };

  const handleShopClick = (shopId) => {
    navigate(`/shops/${shopId}`);
  };

  // Pagination helpers
  const startItem = (pagination.page - 1) * 20 + 1;
  const endItem = Math.min(pagination.page * 20, pagination.total);

  if (loading && shops.length === 0) {
    return (
      <div className="loading-container-shops">
        <LoadingSpinner size="large" message="Loading shops..." />
      </div>
    );
  }

  return (
    <div className="container-shops">
      {/* Header + Toolbar */}
      <header className="header-shops">
        <div className="title-row-shops">
          <nav className="breadcrumbs-shops">
            <Link to="/dashboard" className="breadcrumb-link-shops">Dashboard</Link>
            <span className="breadcrumb-sep-shops">/</span>
            <span className="breadcrumb-current-shops">Shops</span>
          </nav>
          <div className="title-group-shops">
            <h1 className="title-shops">Shops</h1>
            {pagination.total > 0 && (
              <span className="count-badge-shops">{pagination.total}</span>
            )}
          </div>
        </div>

        <div className="toolbar-shops">
          <form onSubmit={handleSearch} className="search-form-shops">
            <div className="search-wrapper-shops">
              <svg
                className="search-icon-shops"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search by LID, name, city, or address..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input-shops"
              />
            </div>
          </form>

          <select
            value={stateFilter}
            onChange={(e) => {
              const v = e.target.value;
              setStateFilter(v);
              fetchShops(1, { stateFilter: v });
            }}
            className="state-filter-shops"
          >
            <option value="">All States</option>
            {uniqueStates.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>

          <select
            value={geocodeFilter}
            onChange={(e) => {
              const v = e.target.value;
              setGeocodeFilter(v);
              fetchShops(1, { geocodeFilter: v });
            }}
            className="state-filter-shops"
            title="Filter by geocoding status"
          >
            <option value="geocoded">Geocoded</option>
            <option value="not-geocoded">Not geocoded</option>
            <option value="all">All</option>
          </select>
        </div>
      </header>

      {error && <div className="error-shops">{error}</div>}

      {/* Table Card */}
      <div className="table-card-shops">
        {shops.length === 0 && !loading ? (
          <div className="empty-state-shops">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="empty-icon-shops"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <p className="empty-title-shops">No shops found</p>
            <p className="empty-text-shops">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        ) : (
          <>
            <div className="table-scroll-shops">
              <table className="table-shops">
                <thead>
                  <tr>
                    <th>LID</th>
                    <th>Shop Name</th>
                    <th>Address</th>
                    <th>City</th>
                    <th>State</th>
                    <th>ZIP</th>
                    <th>Radius</th>
                    <th>Postcards Left</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {shops.map((shop, index) => (
                    <tr
                      key={shop._id}
                      onClick={() => handleShopClick(shop._id)}
                      className="row-shops"
                      style={{ animationDelay: `${index * 30}ms` }}
                    >
                      {/* Service Intel locationId — the "lid" the parent sends
                          in the iframe payload, and how the team identifies a
                          shop. Also searchable in the box above. */}
                      <td className="lid-cell-shops">
                        {shop.locationId ?? <span className="unknown-shops">—</span>}
                      </td>
                      <td className="name-cell-shops">{shop.shopName}</td>
                      <td>{shop.address}</td>
                      <td>{shop.city}</td>
                      <td>{shop.state}</td>
                      <td>{shop.zipCode}</td>
                      <td>
                        <span className="radius-cell-shops">
                          {shop.radiusMiles} mi
                        </span>
                      </td>
                      <td>{renderPostcardsLeft(shop.postcardQuota)}</td>
                      <td>
                        <span
                          className={`status-badge-shops ${
                            shop.isGeocoded ? 'geocoded-shops' : 'not-geocoded-shops'
                          }`}
                        >
                          <span className="status-dot-shops" />
                          {shop.isGeocoded ? 'Geocoded' : 'Not Geocoded'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination inside card */}
            {pagination.pages > 1 && (
              <div className="pagination-shops">
                <span className="page-info-shops">
                  Showing {startItem}–{endItem} of {pagination.total} shops
                </span>
                <div className="page-actions-shops">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="page-btn-shops"
                    aria-label="Previous page"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                    Prev
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    className="page-btn-shops"
                    aria-label="Next page"
                  >
                    Next
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Shops;
